import { Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { SpaContext } from '../Classes/spacontext';
import { SpaLogger } from '../Common/Classes/Global/spalogger.class';
import { VisibaInputComponent } from '../Common/Directives/visibaInput';
import { Helper } from '../Common/Helpers/helper';
import { Messaging } from '../Injectables/Models/Messaging/messaging.models';
import { MessagingInjectable } from '../Injectables/messaging.injectable';
import { MessagingConversationApiModel } from '../Services/api/v2/Models/MessagingConversationApiModel';
import { MessagingServiceV2 } from '../Services/api/v2/messaging.service';
import { DynamicFormServiceV2 } from '../Services/api/v2/Units/dynamic-form.service';
import { UserFilesService } from '../Services/api/v2/userfiles.service';
// import { MatomoTracker } from 'ngx-matomo';
import { AuthContextService } from '../Services/authContext.service';
import { ResponseStatus } from '../Services/Models/Appointment/ResponseStatus';
import { DynamicFormData } from './DynamicForm/DynamicFormData';
import { DynamicFormOutcome } from './DynamicForm/DynamicFormOutcome';

@Component({
	standalone: false,
	selector: 'messaging',
	templateUrl: '../ComponentHtml/Messaging.html',
	styleUrls: ['../Css/Components/Messaging.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class MessagingComponent {
	case: MessagingConversationApiModel = null;
	_caseId: number = null;
	@Input() set caseId(value: number) {
		if (typeof value === 'string') {
			value = Number(value);
		}
		this._caseId = value;
		this.selectCase(this._caseId);
	}
	get caseId(): number {
		return this._caseId;
	}
	@ViewChild('messageArea', { static: false }) messageArea: ElementRef;
	@ViewChildren('messageList') messageList;
	@ViewChild('inputfield', { static: false }) inputfield: VisibaInputComponent;
	@Output()
	public formCompleted = new EventEmitter<number>();

	inputFieldHasFocus: boolean;
	hasConnectionProblem: boolean = false;
	messageText: string;
	messages: Messaging.MessageSocketModel[] = [];
	uploadingMessages: Messaging.MessageSocketModel[] = [];
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	activeForm: { alias: any; formData: DynamicFormData } = null;
	fileUploadError: string = null;
	public isDisabled = false;
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	imageViewer: any = null;
	showDrop: boolean = false;
	isScrolledToBottom: boolean = true;
	private lastReadUntilTimestamp = null;
	private onMessagesSubscription: Subscription;
	private onGetMessagesSubscription: Subscription;
	private onHasReadUntilSubscription: Subscription;
	private onCaseClosedSubscription: Subscription;
	private onCaseAllowClientSubscription: Subscription;

	constructor(
		// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
		private spaLogger: SpaLogger,
		private _messagingInjectable: MessagingInjectable,
		private messagingService: MessagingServiceV2,
		private _dynamicFormService: DynamicFormServiceV2,
		private _userFilesService: UserFilesService,
		// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
		private _spaContext: SpaContext,
		private _ngZone: NgZone,
		// private matomoTracker: MatomoTracker,
		private helper: Helper,
		private authContextService: AuthContextService,
	) {}

	ngOnInit() {
		this.initMessageEvents();
	}

	ngAfterViewInit() {
		this.initTextAreaFocusChecker();

		this.messageList.changes.subscribe(() => {
			this.scrollToBottom();
		});
	}

	ngOnDestroy() {
		this.clearUnreadMessagedAtConversationLeave(this.case);

		this.caseId = null;
		this.clearMessageEvents();
	}

	clearUnreadMessagedAtConversationLeave(p_case: MessagingConversationApiModel) {
		if (this.isScrolledToBottom) {
			this.setReadUntilLastMessageTimestamp();
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	onScroll(event: any) {
		const clientScroll = this.messageArea.nativeElement.scrollTop + this.messageArea.nativeElement.clientHeight;
		const windowScroll = this.messageArea.nativeElement.scrollHeight;
		this.isScrolledToBottom = clientScroll + 35 >= windowScroll;
	}

	private clearMessageEvents() {
		this.onMessagesSubscription.unsubscribe();
		this.onGetMessagesSubscription.unsubscribe();
		this.onHasReadUntilSubscription.unsubscribe();
		this.onCaseClosedSubscription.unsubscribe();
		this.onCaseAllowClientSubscription.unsubscribe();
	}

	private setReadUntilLastMessageTimestamp() {
		if (this.case == null) return;
		if (!this.case['LastMessageTimestamp']) return;
		this.case['LastMessageTimestamp'] = new Date(new Date(this.case['LastMessageTimestamp']).getTime() + 20);
		this._messagingInjectable.setReadUntil(this.case.Id, this.case['LastMessageTimestamp']);
	}

	private initMessageEvents() {
		this.onMessagesSubscription = this._messagingInjectable.onMessages.subscribe((caseMessages: Messaging.CaseMessagesSocketModel) => {
			if (caseMessages.caseId === this.caseId) {
				// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
				let lastReceivedMessageWasRead: boolean =
					new Date(this.case.HasReadUntilTimestamp).valueOf() >= new Date(this.messages[this.messages.length - 1].timestamp).valueOf();

				this.setMessages(caseMessages.data, true);
				this.case['LastMessageTimestamp'] = caseMessages.data[caseMessages.data.length - 1].timestamp;

				let lastMessage: Messaging.MessageSocketModel = caseMessages.data[caseMessages.data.length - 1];
				this.lastReadUntilTimestamp = lastMessage.timestamp;
				if (lastMessage.sender?.isMe === false) {
					//Messages sent by me automaticly sets readUntil value, so there is no need to set read until timestamp if the sender is me.
					if (this.isScrolledToBottom) {
						this.clearUnreadClasses();
					}
				} else {
					this.clearUnreadClasses();
				}
			}
		});
		this.onGetMessagesSubscription = this._messagingInjectable.onGetMessages.subscribe(
			(caseMessages: Messaging.CaseMessagesSocketModel) => {
				if (caseMessages === null) {
					let message: Messaging.MessageSocketModel = {
						id: null,
						sender: {
							id: this.case.Id,
							isMe: false,
							name: this.authContextService.patientIdentity.CommonName,
							profileImageUrl: null,
							title: null,
						},
						text: '',
						textFormat: null,
						timestamp: new Date(),
						files: [],
						responseStatuses: [],
						isSystemMessage: false,
						redRobinSessionId: null,
					};
					this.messages = [message];
					return;
				}

				if (caseMessages.caseId === this.caseId) {
					this.setMessages(caseMessages.data);
					this.case['LastMessageTimestamp'] = caseMessages.data[caseMessages.data.length - 1].timestamp;
					this.scrollToBottom();
				}
			},
		);
		this.onHasReadUntilSubscription = this._messagingInjectable.onHasReadUntil.subscribe(
			(readUntil: Messaging.HasReadUntilSocketModel) => {
				if (readUntil.caseId === this.caseId) {
					this.case.HasReadUntilTimestamp = readUntil.data.timestamp;
				}
			},
		);
		this.onCaseAllowClientSubscription = this._messagingInjectable.onAllowClient.subscribe(
			({ caseId, data }: Messaging.AllowClientResponseChangedModel) => {
				if (this.caseId === caseId) {
					this.isDisabled = !data.allowClientResponse;
				}
			},
		);

		this.onCaseClosedSubscription = this._messagingInjectable.onCaseClosed.subscribe((caseId: number) => {
			if (caseId === this.caseId) {
				this.case.CompletedTimestamp = new Date().toUTCString();
			}
		});
	}

	allMessages() {
		return this.messages.concat(this.uploadingMessages);
	}

	private setMessages(messages: Array<Messaging.MessageSocketModel>, append: boolean = false) {
		messages.forEach((message) => {
			message.text = this.helper.html.stripHtml(message.text);
			message.files.forEach((file) => (file.sizeFormatted = this.helper.file.stringFromBytes(file.length)));
		});

		if (!append) this.messages = messages;
		else {
			for (let i in messages) {
				this.messages.push(messages[i]);
			}
		}
	}

	private initTextAreaFocusChecker() {
		if (!this.inputfield) return;

		let textArea = this.inputfield.elem.nativeElement.getElementsByTagName('textarea')[0];

		textArea.onfocus = () => {
			this.inputFieldHasFocus = true;
		};
		textArea.onblur = () => {
			this.inputFieldHasFocus = false;
		};
	}

	private selectCase(caseId: number) {
		if (this.case !== null && caseId !== null) {
			this.clearUnreadMessagedAtConversationLeave(this.case);
		}
		this.uploadingMessages = [];
		this.messages = [];
		this.activeForm = null;
		this.isDisabled = false;

		if (this.caseId === null) return;

		this.messagingService.get(this.caseId).subscribe((response) => {
			this.case = response.data;
			this.isDisabled = !response.data.AllowClientResponse;

			this._messagingInjectable.getMessages(this.caseId);

			for (let i in this.case.Forms) {
				let respondent = this.case.Forms[i];
				if (typeof respondent['Header'] === 'undefined') {
					this._dynamicFormService.get(respondent.FormId).subscribe(
						(response) => {
							respondent['Header'] = response.data.Header;
						},
						// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
						(_) => {},
					);
				}
			}
		});
	}

	public sendMessage(ev: KeyboardEvent = null, files: Array<Guid> = []) {
		if (this.isDisabled) return;

		if (ev !== null) ev.preventDefault();
		if (files.length > 0) {
			this._messagingInjectable.sendMessage(this.caseId, '', files);
		} else if (this.messageText !== '' && this.messageText !== null) {
			this._messagingInjectable.sendMessage(this.caseId, this.helper.html.stripHtml(this.messageText), files);
			this.messageText = '';
		}
	}

	public getSecondsToEarlier(index: number) {
		if (index === 0) {
			return 0;
		}

		return (
			(new Date(this.allMessages()[index].timestamp).valueOf() - new Date(this.allMessages()[index - 1].timestamp).valueOf()) * 0.001
		);
	}

	public scrollToBottom() {
		if (this.messageArea && this.isScrolledToBottom) {
			this.messageArea.nativeElement.scrollTop = this.messageArea.nativeElement.scrollHeight;
			this.setReadUntilLastMessageTimestamp();
		}
	}

	public trackByMessageId(index: number, message: Messaging.MessageSocketModel): number {
		return message.id;
	}

	public clearUnreadClasses() {
		for (let i = this.messages.length - 1; i >= 0; i--) {
			if (new Date(this.messages[i].timestamp).valueOf() < new Date(this.lastReadUntilTimestamp).valueOf()) {
				this.lastReadUntilTimestamp = this.case.HasReadUntilTimestamp;
				return;
			}
			this.messages[i]['setRead'] = true;
		}
		this.lastReadUntilTimestamp = this.case.HasReadUntilTimestamp;
	}

	public compareReadUntilAndMessageTime(readUntilTime: string, messageTime: string) {
		return new Date(readUntilTime) < new Date(messageTime);
	}

	showMessagingForm(form: Messaging.ResponseStatus) {
		this.activeForm = {
			alias: form.formAlias,
			formData: { RespondantId: form.respondantId, Abortable: true },
		};
	}

	showForm(form: ResponseStatus) {
		this.activeForm = {
			alias: form.FormAlias,
			formData: { RespondantId: form.RespondantId, Abortable: true },
		};
	}

	formFinished = (outcome: DynamicFormOutcome) => {
		const findForm = (): void => {
			let responseStatus: Messaging.ResponseStatus;

			this.messages.find((message) => {
				const statuses = message.responseStatuses.find((responseStatus) => responseStatus.formAlias === this.activeForm.alias);

				if (statuses) {
					responseStatus = statuses;
					return true;
				}
				return false;
			});

			if (responseStatus) {
				responseStatus.completed = true;
			}

			const caseForm = this.case.Forms?.find((form) => form.FormId === this.activeForm.alias);

			if (caseForm) {
				caseForm.Completed = true;
			}
		};

		if (outcome === 'skipped' || outcome === 'completed') {
			findForm();
		}

		this._messagingInjectable.onCompletedForm.emit(this.caseId);

		this.formCompleted.emit(this.caseId);

		this.activeForm = null;
	};

	public trackClickedUpload() {
		// this.matomoTracker.trackEvent('Uploads conversation', `Dialog opened`);
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	upload(event: any) {
		let files = typeof event.dataTransfer === 'undefined' ? event.target.files : event.dataTransfer.files;

		for (let i = 0; i < files.length; i++) {
			this._userFilesService.getFileIdentifier(files[i].name, files[i].size, files[i].type).subscribe((response) => {
				const fileId = response.data.Id;
				const uploadMessage: Messaging.MessageSocketModel = {
					id: 0,
					sender: {
						id: this.case.Id,
						isMe: true,
						name: this.authContextService.patientIdentity.CommonName,
						profileImageUrl: null,
						title: null,
					},
					text: '',
					textFormat: '',
					timestamp: new Date(),
					files: [
						{
							url: '',
							fileName: files[i].name,
							length: files[i].size,
							type: 'uploading',
							sizeFormatted: this.helper.file.stringFromBytes(files[i].size),
							failed: false,
							progress: 0,
							id: fileId,
						},
					],
					responseStatuses: [],
					isSystemMessage: false,
					redRobinSessionId: null,
				};
				this.uploadingMessages.push(uploadMessage);
				window.setTimeout(() => {
					this.scrollToBottom();
				});

				// Check file size 100mb
				if (files[i].size > 100 * 1000 * 1024) {
					uploadMessage.files[0].failed = true;
					window.setTimeout(() => {
						this.uploadingMessages = this.uploadingMessages.filter((message) => message.files[0].id !== fileId);
					}, 5000);

					return;
				}

				this._userFilesService.uploadFiles(response.data.UploadUrl, [files[i]], true).subscribe(
					(response) => {
						if (typeof response === 'number') {
							this._ngZone.run(() => {
								uploadMessage.files[0].progress = response;
							});
						} else {
							this.uploadingMessages = this.uploadingMessages.filter((message) => message.files[0].id !== fileId);
							this.sendMessage(null, [fileId]);
						}
						// this.matomoTracker.trackEvent('Uploads conversation', 'Upload completed');
					},
					(error) => {
						uploadMessage.files[0].failed = true;
						window.setTimeout(() => {
							this.uploadingMessages = this.uploadingMessages.filter((message) => message.files[0].id !== fileId);
						}, 5000);
					},
				);
			});
		}

		this.showDrop = false;

		return false;
	}

	viewImages(startImageUrl) {
		let imageFiles = this.messages
			.map((message) => message.files)
			.reduce((a, b) => {
				return a.concat(b);
			}, []);

		let viewImagesStart = 0;
		let images = imageFiles.map((item, index) => {
			if (item.url === startImageUrl) viewImagesStart = index;

			return { uploader: null, viewUrl: item.url, downloadUrl: item.url, fileName: item.fileName, type: item.type };
		});

		// const fileType = images[viewImagesStart].type.toLowerCase().indexOf('image') > -1 ? 'image' : 'file';
		// this.matomoTracker.trackEvent('Uploads conversation', `Show ${fileType} clicked`);
		this.imageViewer = { images: images, startIndex: viewImagesStart };
	}
}
